import React, { ReactNode } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import styled from 'styled-components';
import { ButtonText } from 'components/Button';
import { theme, toRem } from 'design';
import ErrorIllustration from 'images/illustrations/not-found.svg';
import rollbar from 'lib/ErrorLogger';
import { URL } from 'routes/routeConstants';

interface IErrorBoundaryProps extends RouteComponentProps {
  children: ReactNode;
  boundaryName?: string;
  fallback?: ReactNode;
}

interface IErrorBoundaryState {
  hasError: boolean;
}

const Wrapper = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
  align-items: center;
  width: 100%;
  min-height: 100vh;
  padding: ${toRem(theme.space.xl)};
  text-align: center;
  color: ${theme.color.neutral[900]};
`;

const Illustration = styled.img`
  width: 60%;
  max-width: ${toRem(260)};
  margin-bottom: ${toRem(theme.space['3xl'])};

  @media (min-width: ${theme.breakpoint.small}px) {
    width: 50%;
    max-width: ${toRem(320)};
  }

  @media (min-width: ${theme.breakpoint.med}px) {
    width: 40%;
    max-width: ${toRem(380)};
  }
`;

const Title = styled.h1`
  margin-bottom: ${toRem(theme.space.sm)};
  font-size: ${toRem(theme.font.size['3xl'])};
  font-weight: ${theme.font.weight.semiBold};
`;

const Message = styled.p`
  margin-bottom: ${toRem(theme.space.lg)};
  max-width: ${toRem(480)};
  font-size: ${toRem(theme.font.size.md)};
  color: ${theme.color.neutral[600]};
`;

const Actions = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: center;
  align-items: center;
  gap: ${toRem(theme.space.md)};
`;

class ErrorBoundaryInner extends React.Component<IErrorBoundaryProps, IErrorBoundaryState> {
  public state: IErrorBoundaryState = {
    hasError: false,
  };

  static getDerivedStateFromError(): IErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    const { boundaryName, location } = this.props;

    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.error(error, info);
    }

    try {
      const metadata = {
        boundaryName: boundaryName || 'UnknownBoundary',
        componentStack: info.componentStack,
        url: window.location.href,
        path: location?.pathname,
      };

      rollbar.error(error, metadata);

      if (window.FS) {
        window.FS('trackEvent', {
          name: 'Application Error',
          properties: {
            ...metadata,
            message: error.message,
            name: error.name,
          },
        });
      }
    } catch {
      // Swallow logging errors to avoid breaking the boundary itself
    }
  }

  render(): ReactNode {
    const { children, fallback } = this.props;
    const { hasError } = this.state;

    if (!hasError) return children;

    if (fallback) return fallback;

    return (
      <Wrapper data-cy="error-boundary">
        <Illustration src={ErrorIllustration} alt="Application error illustration" aria-hidden="true" data-cy="error-boundary-illustration" />
        <Title data-cy="error-boundary-title">Oh no! Something went wrong!</Title>
        <Message data-cy="error-boundary-message">We've been notified of the problem and are looking into it. Please try returning to your dashboard.</Message>
        <Actions>
          <ButtonText
            data-cy="error-boundary-button"
            label="Go to dashboard"
            onClick={() => {
              window.location.href = URL.RootPrivate;
            }}
          />
        </Actions>
      </Wrapper>
    );
  }
}

export const ErrorBoundary = withRouter(ErrorBoundaryInner);
